import { useId, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiProviderConfig, AiProviderKind } from '@mailpilot/shared';
import { Cpu, ListRestart } from 'lucide-react';
import { api, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { Button, Checkbox, Field, InlineError, Input, Modal, Notice, Select, useToast } from '../ui';
import { SubSection } from './SettingsPrimitives';

export type KindInfo = ResOf<'GET /api/ai/providers'>['kinds'][number];

interface Form {
  kind: AiProviderKind;
  label: string;
  endpoint: string;
  apiKey: string;
  defaultModel: string;
  timeoutSec: string;
  budget: string;
  fallbackProviderId: string;
  fallbackModel: string;
  allowFallback: boolean;
}

export function showsEndpoint(k: KindInfo | undefined): boolean {
  return !!k && (k.needsEndpoint || k.kind === 'openai_compatible' || k.kind === 'local_agent');
}

function initial(p: AiProviderConfig | null, kinds: KindInfo[]): Form {
  if (p)
    return {
      kind: p.kind,
      label: p.label,
      endpoint: p.endpoint ?? '',
      apiKey: '',
      defaultModel: p.defaultModel,
      timeoutSec: String(Math.round(p.timeoutMs / 1000)),
      budget: p.monthlyBudgetUsd === null ? '' : String(p.monthlyBudgetUsd),
      fallbackProviderId: p.fallbackProviderId ?? '',
      fallbackModel: p.fallbackModel ?? '',
      allowFallback: p.allowFallback,
    };
  const k = kinds.find((x) => x.kind !== 'demo') ?? kinds[0];
  return {
    kind: k?.kind ?? 'openai',
    label: k?.label ?? '',
    endpoint: k?.defaultEndpoint ?? '',
    apiKey: '',
    defaultModel: k?.suggestedModels[0] ?? '',
    timeoutSec: '60',
    budget: '',
    fallbackProviderId: '',
    fallbackModel: '',
    allowFallback: false,
  };
}

export function AiProviderEditor({
  open,
  onClose,
  provider,
  kinds,
  providers,
}: {
  open: boolean;
  onClose: () => void;
  provider: AiProviderConfig | null;
  kinds: KindInfo[];
  providers: AiProviderConfig[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const listId = useId();
  const [f, setF] = useState<Form>(() => initial(provider, kinds));
  const [touched, setTouched] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((p) => ({ ...p, [k]: v }));
  const kind = kinds.find((k) => k.kind === f.kind);

  const models = useQuery({
    queryKey: qk.aiModels(provider?.id ?? 'none'),
    queryFn: () => api('GET /api/ai/providers/:id/models', { params: { id: provider!.id } }),
    enabled: false,
  });

  const changeKind = (next: AiProviderKind) => {
    const nk = kinds.find((k) => k.kind === next);
    setF((p) => ({
      ...p,
      kind: next,
      label: !p.label || p.label === kind?.label ? (nk?.label ?? p.label) : p.label,
      endpoint: nk?.defaultEndpoint ?? '',
      defaultModel: nk?.suggestedModels[0] ?? '',
    }));
  };

  const save = useMutation({
    mutationFn: () => {
      const timeoutMs = Math.max(1, Number(f.timeoutSec) || 60) * 1000;
      const monthlyBudgetUsd = f.budget.trim() === '' ? null : Number(f.budget);
      const common = {
        label: f.label.trim(),
        endpoint: showsEndpoint(kind) ? f.endpoint.trim() || null : null,
        defaultModel: f.defaultModel.trim(),
        timeoutMs,
        monthlyBudgetUsd,
        fallbackProviderId: f.fallbackProviderId || null,
        fallbackModel: f.fallbackModel.trim() || null,
        allowFallback: f.allowFallback,
        ...(f.apiKey ? { apiKey: f.apiKey } : {}),
      };
      return provider ? api('PATCH /api/ai/providers/:id', { params: { id: provider.id }, body: common }) : api('POST /api/ai/providers', { body: { kind: f.kind, ...common } });
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.aiProviders });
      toast.success(provider ? `${res.provider.label} saved` : `${res.provider.label} added — test the connection next`);
      onClose();
    },
  });

  const errors = {
    label: !f.label.trim() ? 'Required' : undefined,
    model: !f.defaultModel.trim() ? 'Choose a model' : undefined,
    endpoint: kind?.needsEndpoint && !f.endpoint.trim() ? 'This provider needs an endpoint URL' : undefined,
    apiKey: !provider && kind?.needsKey && !f.apiKey ? 'An API key is required' : undefined,
    budget: f.budget.trim() !== '' && !(Number(f.budget) >= 0) ? 'Enter a positive amount' : undefined,
  };
  const invalid = Object.values(errors).some(Boolean);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!invalid) save.mutate();
  };

  const modelOptions = Array.from(new Set([...(models.data?.models ?? []), ...(kind?.suggestedModels ?? [])]));
  const fallbackChoices = providers.filter((p) => p.id !== provider?.id);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={provider ? `Edit ${provider.label}` : 'Add AI provider'}
      description="Your API key is encrypted on the server and never shown again."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="ai-provider-form" loading={save.isPending}>
            {provider ? 'Save provider' : 'Add provider'}
          </Button>
        </>
      }
    >
      <form id="ai-provider-form" onSubmit={submit} className="space-y-5" autoComplete="off">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Provider type" hint={provider ? 'The type cannot be changed after saving.' : undefined}>
            {(id) => (
              <Select id={id} value={f.kind} disabled={!!provider} onChange={(e) => changeKind(e.target.value as AiProviderKind)}>
                {kinds.map((k) => (
                  <option key={k.kind} value={k.kind}>
                    {k.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Label" required error={touched ? errors.label : undefined}>
            {(id) => <Input id={id} value={f.label} onChange={(e) => set('label', e.target.value)} />}
          </Field>
        </div>

        {f.kind === 'local_agent' && (
          <Notice tone="info" icon={<Cpu className="h-4 w-4" aria-hidden />}>
            Local models connect through the secured desktop-agent route: the MailPilot desktop app relays requests to a model running on your computer, instead of a cloud AI provider. Keep the desktop app running for these requests to work.
          </Notice>
        )}

        {showsEndpoint(kind) && (
          <Field label="Endpoint URL" required={kind?.needsEndpoint} error={touched ? errors.endpoint : undefined} hint="Base URL of the API, e.g. https://api.example.com/v1">
            {(id) => <Input id={id} type="url" value={f.endpoint} onChange={(e) => set('endpoint', e.target.value)} placeholder={kind?.defaultEndpoint ?? 'https://'} />}
          </Field>
        )}

        {kind?.needsKey && (
          <Field
            label="API key"
            required={!provider}
            error={touched ? errors.apiKey : undefined}
            hint={provider?.hasApiKey ? `Key saved: ${provider.apiKeyHint ?? '••••'}. Leave blank to keep it.` : 'Stored encrypted on the server; never shown again.'}
          >
            {(id) => <Input id={id} type="password" autoComplete="off" value={f.apiKey} onChange={(e) => set('apiKey', e.target.value)} placeholder={provider?.hasApiKey ? 'Leave blank to keep existing key' : ''} />}
          </Field>
        )}

        <div className="space-y-2">
          <Field label="Default model" required error={touched ? errors.model : undefined}>
            {(id) => <Input id={id} list={listId} value={f.defaultModel} onChange={(e) => set('defaultModel', e.target.value)} />}
          </Field>
          <datalist id={listId}>
            {modelOptions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" icon={<ListRestart className="h-4 w-4" aria-hidden />} disabled={!provider} loading={models.isFetching} onClick={() => void models.refetch()}>
              Load models
            </Button>
            <span className="text-xs text-muted">
              {!provider
                ? 'Available after the provider is saved — MailPilot asks the provider with your stored key.'
                : models.data
                  ? `${models.data.models.length} models loaded — pick from the suggestions.`
                  : 'Fetches the list of models your key can use.'}
            </span>
          </div>
          {models.error ? <InlineError error={models.error} /> : null}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Timeout (seconds)">
            {(id) => <Input id={id} type="number" min={1} max={600} value={f.timeoutSec} onChange={(e) => set('timeoutSec', e.target.value)} />}
          </Field>
          <Field label="Monthly budget (USD)" hint="Leave blank for no limit. Requests stop when the budget is reached." error={touched ? errors.budget : undefined}>
            {(id) => <Input id={id} type="number" min={0} step="0.01" value={f.budget} onChange={(e) => set('budget', e.target.value)} placeholder="No limit" />}
          </Field>
        </div>

        <div className="border-t border-line/70 pt-5">
        <SubSection title="Fallback" hint="Optional second provider to use if this one fails or runs out of budget.">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Fallback provider">
              {(id) => (
                <Select id={id} value={f.fallbackProviderId} onChange={(e) => set('fallbackProviderId', e.target.value)}>
                  <option value="">None</option>
                  {fallbackChoices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Fallback model" hint="Blank = that provider's default model">
              {(id) => <Input id={id} value={f.fallbackModel} onChange={(e) => set('fallbackModel', e.target.value)} />}
            </Field>
          </div>
          <Checkbox
            checked={f.allowFallback}
            onChange={(v) => set('allowFallback', v)}
            label="Allow automatic fallback"
            description="If unchecked, MailPilot fails safely and tells you instead of silently switching providers."
          />
        </SubSection>
        </div>

        <InlineError error={save.error} />
      </form>
    </Modal>
  );
}
